import { Injectable } from "@angular/core";
import { HttpRequest, HttpHandler, HttpEvent, HttpInterceptor, HttpErrorResponse } from "@angular/common/http";
import { Observable } from "rxjs";
import { catchError } from "rxjs/operators";
import { CristieError } from "./cristie-error";

@Injectable()
export class ErrorStandardiser implements HttpInterceptor {
  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<CristieError>> {
    return next.handle(request).pipe(
      catchError(async (res) => {
        // If we don't have an object, there is little we can
        // this includes if we have a string, as its almost certainly not
        // sensible information for the user
        if (!res || typeof res != "object") {
          throw new CristieError(0, "Server response could not be understood.", "An unknown response was sent by the server.", "", "");
        }

        // Special case where Angular fails in an earlier call
        if (res.status == 0 && res.url.includes("validateconnection")) {
          res.message = "SSL connection error";
          throw new CristieError(
            -1,
            "SSL connection error",
            "This can sometimes happen when your browser requires you to accept new/modified or unsigned SSL sertificate.",
            "",
            "",
          );
        }

        if (res.error && typeof res.error == "object") {
          let errorObj = res.error;
          let message: string;
          const errorCode = ""; // It would be nice to start adding error codes into our errors so we can just stick them on the forum
          let possibleValues: string;

          if (res instanceof HttpErrorResponse && res.error instanceof Blob && res.error.type === "application/json") {
            // If an endpoint returns a stream then the error response may be a blob
            // Convert blob json error to a usable object
            errorObj = await decodeBlobJson(res);
          }

          if (errorObj.possibleValues) {
            possibleValues = errorObj.possibleValues.join(", ");
          }

          // This is one of our errors
          // If we have a message in the data, that is better than just the status text
          if (errorObj.message) {
            if (errorObj.possibleValues) message = `${errorObj.message}. Possible values: ${possibleValues}`;
            else message = errorObj.message;
            throw new CristieError(res.status, "Cristie Error", message, errorCode, possibleValues);
          }

          if (errorObj.description) {
            message = errorObj.description;
            // If we have a field, then its a missing field. So show the user what field is missing too
            if (errorObj.field) {
              // Template strings are the future https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Template_literals
              if (errorObj.possibleValues) message = `${errorObj.description} ${errorObj.field}. Possible values: ${possibleValues}`;
              else message = `${errorObj.description} ${errorObj.field}`;
            }
            if (errorObj.resource) {
              // Template strings are the future https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Template_literals
              message = `${errorObj.description} Resource: ${errorObj.resource}`;
            }

            throw new CristieError(res.status, "Cristie Error", message, errorCode, possibleValues);
          }

          // If we have a message in the data, that is better than just the status text
          if (errorObj.message) {
            throw new CristieError(res.status, "Cristie Error", errorObj.message, errorCode, possibleValues);
          }
        }

        // All HTTP response objects should have this
        if (res.statusText && res.status !== 0) {
          if (res.statusText == "Proxy Error") res.statusText = "Timed out whilst contacting VA - please check service status";
          throw new CristieError(res.status, "Generic Error ", res.statusText, "", "");
        }

        // Unknown response
        throw new CristieError(0, "Server response could not be understood.", "An unknown response was sent by the server.", "", "");
      }),
    );
  }
}

function decodeBlobJson(error: any) {
  if (error instanceof HttpErrorResponse && error.error instanceof Blob && error.error.type === "application/json") {
    // https://github.com/angular/angular/issues/19888
    return new Promise<any>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e: Event) => {
        try {
          resolve(JSON.parse((e.target as any).result));
        } catch {
          console.log("Could not decode blob error");
          reject(error);
        }
      };
      reader.onerror = () => {
        reject(error);
      };
      reader.readAsText(error.error);
    });
  }
}
